/**
 * Hierarchical, lazily loaded 3D object tree for the linkage picker.
 *
 * Nodes come one level at a time (`/objects/tree?parentDbId=`), so browsing a
 * 7.5k-object model costs one small request per expansion instead of shipping
 * the whole hierarchy. Ticking a node adopts everything underneath it;
 * selecting a node drives the live 3D viewer so the picker and the model always
 * agree on what is being linked.
 */
import { useState, useCallback } from 'react';
import { authFetch } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { ChevronRight, ChevronDown, Loader2, Box, Layers, Crosshair } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export function ModelObjectTree({ projectId, modelId, viewerRef, pickedIds, onAdd, onRemove }) {
  const base = `${API}/api/v1/projects/${projectId}/4d/models/${modelId}/cognara`;
  const [children, setChildren] = useState({});     // parentKey → nodes
  const [open, setOpen] = useState(() => new Set());
  const [busy, setBusy] = useState(() => new Set());
  // A branch is "ticked" because WE adopted its leaves — the branch's own dbId
  // is never in the linked set, so the checkbox has to remember what it added
  // in order to take it back out.
  const [adopted, setAdopted] = useState({});   // branch dbId → adopted leaf ids

  const fetchLevel = useCallback(async (parentDbId) => {
    const key = parentDbId == null ? 'root' : String(parentDbId);
    if (children[key]) return;
    setBusy(prev => new Set(prev).add(key));
    try {
      const url = parentDbId == null ? `${base}/objects/tree` : `${base}/objects/tree?parentDbId=${parentDbId}`;
      const r = await authFetch(url);
      const d = await r.json().catch(() => ({}));
      if (r.ok) setChildren(prev => ({ ...prev, [key]: d.nodes || [] }));
      else toast.error(d.detail || 'Could not read the model tree');
    } catch { toast.error('Could not read the model tree'); }
    setBusy(prev => { const n = new Set(prev); n.delete(key); return n; });
  }, [base, children]);

  // Load the root once on first render.
  if (!children.root && !busy.has('root')) fetchLevel(null);

  const toggle = async (node) => {
    const key = String(node.dbId);
    if (open.has(key)) {
      setOpen(prev => { const n = new Set(prev); n.delete(key); return n; });
      return;
    }
    await fetchLevel(node.dbId);
    setOpen(prev => new Set(prev).add(key));
  };

  const adopt = async (node) => {
    setBusy(prev => new Set(prev).add(`pick-${node.dbId}`));
    try {
      const r = await authFetch(`${base}/objects/descendants?dbId=${node.dbId}&leavesOnly=true`);
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { toast.error(d.detail || 'Could not resolve that branch'); }
      else {
        const objs = d.objects || [];
        onAdd(objs);
        setAdopted(prev => ({ ...prev, [node.dbId]: objs.map(o => o.dbId) }));
        toast.success(`${objs.length} object(s) from ${node.name}`);
      }
    } catch { toast.error('Could not resolve that branch'); }
    setBusy(prev => { const n = new Set(prev); n.delete(`pick-${node.dbId}`); return n; });
  };

  const release = (node) => {
    const ids = adopted[node.dbId];
    if (ids?.length) {
      onRemove(ids);
      setAdopted(prev => { const n = { ...prev }; delete n[node.dbId]; return n; });
      toast.success(`${ids.length} object(s) removed from ${node.name}`);
      return;
    }
    onRemove([node.dbId]);
  };

  const showInViewer = (node) => {
    const ok = viewerRef?.current?.selectAndZoom?.(node.dbId);
    if (ok === false) toast.info('Load the 3D model to preview this object.');
  };

  const renderLevel = (parentKey, depth) => {
    const nodes = children[parentKey];
    if (busy.has(parentKey) && !nodes) {
      return (
        <div className="px-3 py-2" style={{ paddingLeft: `${10 + depth * 14}px` }}>
          <Loader2 className="w-3 h-3 animate-spin text-muted-foreground" />
        </div>
      );
    }
    if (!nodes) return null;
    return nodes.map(node => {
      const key = String(node.dbId);
      const isOpen = open.has(key);
      const isPicked = !!adopted[node.dbId] || pickedIds?.has(node.dbId);
      return (
        <div key={key}>
          <div className="flex items-center gap-1.5 pr-2 py-1 border-b hover:bg-muted/40 group"
               style={{ paddingLeft: `${8 + depth * 14}px` }}
               data-testid={`model-tree-node-${node.dbId}`}>
            {node.isLeaf ? <span className="w-3.5 shrink-0" /> : (
              <button onClick={() => toggle(node)} className="shrink-0 text-muted-foreground hover:text-foreground"
                      data-testid={`model-tree-toggle-${node.dbId}`}>
                {busy.has(key) ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  : isOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
              </button>
            )}
            <Checkbox checked={!!isPicked}
                      onCheckedChange={(v) => { if (v) adopt(node); else release(node); }}
                      className="shrink-0" data-testid={`model-tree-check-${node.dbId}`} />
            {node.isLeaf ? <Box className="w-3 h-3 text-muted-foreground shrink-0" />
                         : <Layers className="w-3 h-3 text-blue-500 shrink-0" />}
            <button onClick={() => showInViewer(node)}
                    className="text-[11px] font-mono truncate text-left hover:underline"
                    title="Show in the 3D model"
                    data-testid={`model-tree-show-${node.dbId}`}>
              {node.name}
            </button>
            {!node.isLeaf && (
              <span className="text-[10px] text-muted-foreground shrink-0">{node.childCount} kids</span>
            )}
            <Button size="icon" variant="ghost"
                    className="h-5 w-5 ml-auto shrink-0 opacity-0 group-hover:opacity-100"
                    onClick={() => showInViewer(node)} title="Zoom to this in the 3D model">
              <Crosshair className="w-3 h-3" />
            </Button>
          </div>
          {isOpen && renderLevel(key, depth + 1)}
        </div>
      );
    });
  };

  return <div data-testid="model-object-tree">{renderLevel('root', 0)}</div>;
}

export default ModelObjectTree;
